import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RowActions from "@/components/RowActions";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Users, ChevronRight, BellRing } from "lucide-react";
import BrainTypeField from "@/components/BrainTypeField";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", brain_type: null, location: "" };

export default function InstituteCoaches() {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coaches");
      setCoaches(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, email: c.email, password: "", brain_type: c.brain_type ?? null, location: c.location ?? "" });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, location: form.location };
        if (form.password) payload.password = form.password;
        if (form.brain_type != null) payload.brain_type = form.brain_type;
        await api.put(`/coaches/${editing.id}`, payload);
        toast.success("Coach updated");
      } else {
        const payload = { ...form };
        if (payload.brain_type == null) delete payload.brain_type;
        await api.post("/coaches", payload);
        toast.success("Coach added");
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/coaches/${confirmTarget.id}`);
      toast.success("Coach deleted");
      setConfirmTarget(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-12 max-w-7xl" data-testid="institute-coaches-page">
      <PageHeader
        overline="Institute Admin"
        title="Coaches"
        description="Onboard coaches who will work with clients in your institute."
        action={
          <Button
            onClick={openCreate}
            data-testid="create-coach-button"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
          >
            <Plus strokeWidth={1.5} size={18} className="mr-2" />
            Add Coach
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5E2DC] sm:max-w-lg" data-testid="coach-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold">
              {editing ? "Edit Coach" : "Add Coach"}
            </DialogTitle>
            <DialogDescription className="text-[#5C635A]">
              {editing
                ? "Update the coach's details. Leave password blank to keep it unchanged."
                : "Creates a coach account scoped to this institute."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <Field label="Full Name" testid="coach-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Email" type="email" testid="coach-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <Field
              label={editing ? "New Password (optional)" : "Password"}
              type="password"
              testid="coach-password-input"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              required={!editing}
              placeholder={editing ? "Leave blank to keep current" : ""}
            />
            <BrainTypeField
              value={form.brain_type}
              onChange={(v) => setForm({ ...form, brain_type: v })}
              testid="coach-brain-type-input"
              label="Brain Type (1–9, optional)"
            />
            <Field
              label="Location (optional)"
              testid="coach-location-input"
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              placeholder="City, country — visible to prospects during registration"
            />
            {error && (
              <p
                className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                data-testid="coach-error"
              >
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="coach-submit-button"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                {saving ? "Saving..." : editing ? "Save changes" : "Add Coach"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
        title="Delete this coach?"
        description={
          confirmTarget
            ? `This will permanently delete "${confirmTarget.name}" along with all their clients and onboarding responses. This action cannot be undone.`
            : ""
        }
        onConfirm={handleDelete}
        loading={deleting}
        testIdPrefix="coach-delete"
      />

      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="coaches-list">
        <div className="grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold border-b border-[#E5E2DC]">
          <div className="col-span-5">Coach</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2 text-right">Joined</div>
          <div className="col-span-1"></div>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-[#8A9188]">Loading coaches…</div>
        ) : coaches.length === 0 ? (
          <div className="px-6 py-16 text-center" data-testid="coaches-empty">
            <Users strokeWidth={1.5} className="mx-auto text-[#B37A5F] mb-3" size={32} />
            <p className="text-[#5C635A]">No coaches yet. Add your first one.</p>
          </div>
        ) : (
          coaches.map((c) => (
            <div
              key={c.id}
              onClick={() => navigate(`/details/coach/${c.id}`)}
              className="grid grid-cols-12 px-6 py-5 border-b last:border-0 border-[#E5E2DC] items-center hover:bg-[#F9F8F6] transition-colors cursor-pointer group"
              data-testid={`coach-row-${c.id}`}
            >
              <div className="col-span-5 font-medium text-[#1A1D1A] flex items-center gap-2 flex-wrap">
                {c.name}
                <ChevronRight strokeWidth={1.5} size={14} className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity" />
                {c.pending_requests_count > 0 && (
                  <span
                    data-testid={`coach-pending-${c.id}`}
                    title={`${c.pending_requests_count} pending client request${c.pending_requests_count === 1 ? "" : "s"}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#F5EAE2] text-[#B37A5F] border border-[#E5D2C2]"
                  >
                    <BellRing strokeWidth={2} size={10} />
                    {c.pending_requests_count} pending
                  </span>
                )}
              </div>
              <div className="col-span-4 text-sm text-[#5C635A] truncate">{c.email}</div>
              <div className="col-span-2 text-right text-xs text-[#8A9188] uppercase tracking-wider">
                {new Date(c.created_at).toLocaleDateString()}
              </div>
              <div className="col-span-1 flex justify-end">
                <RowActions
                  onEdit={() => openEdit(c)}
                  onDelete={() => setConfirmTarget(c)}
                  testIdPrefix={`coach-${c.id}`}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
